/**
 * ProfileEditor.tsx - Profile Editor Component
 * 
 * CHANGE HISTORY:
 * - 251102_1706 danielbeer: Create ProfileEditor component
 * 
 * Visual editor for creating and editing profiles
 */

import React, { useState, useEffect } from 'react';
import type { Profile, ProfileTemplate } from '@opcentral/profylr';

export interface ProfileEditorProps {
  /** Profile to edit (null for new profile) */
  profile?: Profile | ProfileTemplate | null;
  
  /** Callback when profile is saved */
  onSave: (profileData: Omit<Profile, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  
  /** Callback when editing is cancelled */
  onCancel?: () => void;
  
  /** Show advanced options */
  showAdvanced?: boolean;
  
  /** Custom className */
  className?: string;
}

/**
 * ProfileEditor - Visual profile editor
 * 
 * @example
 * ```tsx
 * <ProfileEditor
 *   profile={existingProfile}
 *   onSave={async (data) => {
 *     await profylr.update(profile.id, data);
 *   }}
 *   onCancel={() => setEditing(false)}
 * />
 * ```
 */
export const ProfileEditor: React.FC<ProfileEditorProps> = ({
  profile,
  onSave,
  onCancel,
  showAdvanced = false,
  className = ''
}) => {
  const [formData, setFormData] = useState<any>({
    name: '',
    description: '',
    scope: 'app',
    tags: [],
    dataBoundaries: {
      datasets: [],
      excludeDatasets: [],
      tags: [],
      dateRange: {}
    },
    perspective: {},
    context: {},
    access: {},
    behavior: {}
  });
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Load profile data
  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || '',
        description: profile.description || '',
        scope: profile.scope || 'app',
        tags: profile.tags || [],
        dataBoundaries: profile.dataBoundaries || {},
        perspective: profile.perspective || {},
        context: profile.context || {},
        access: profile.access || {},
        behavior: profile.behavior || {}
      });
    }
  }, [profile]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    
    try {
      // Validate required fields
      if (!formData.name) {
        throw new Error('Profile name is required');
      }
      
      await onSave(formData);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  const updateField = (path: string, value: any) => {
    setFormData((prev: any) => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };
  
  const addArrayItem = (path: string, value: string) => {
    if (!value.trim()) return;
    
    const current = path.split('.').reduce((obj, key) => obj[key], formData);
    const newArray = [...(current || []), value.trim()];
    updateField(path, newArray);
  };
  
  const removeArrayItem = (path: string, index: number) => {
    const current = path.split('.').reduce((obj, key) => obj[key], formData);
    const newArray = current.filter((_: any, i: number) => i !== index);
    updateField(path, newArray);
  };
  
  return (
    <div className={`profile-editor ${className}`}>
      <form onSubmit={handleSubmit} className="profile-editor-form">
        {/* Header */}
        <div className="profile-editor-header">
          <h2>{profile ? 'Edit Profile' : 'Create Profile'}</h2>
        </div>
        
        {error && (
          <div className="profile-editor-error">
            {error}
          </div>
        )}
        
        {/* Basic Information */}
        <section className="profile-editor-section">
          <h3>Basic Information</h3>
          
          <div className="profile-editor-field">
            <label htmlFor="name">Name *</label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="e.g., marketing-messaging"
              required
            />
          </div>
          
          <div className="profile-editor-field">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Describe this profile's purpose"
              rows={3}
            />
          </div>
          
          <div className="profile-editor-field">
            <label htmlFor="scope">Scope</label>
            <select
              id="scope"
              value={formData.scope}
              onChange={(e) => updateField('scope', e.target.value)}
            >
              <option value="global">Global</option>
              <option value="app">App</option>
              <option value="view">View</option>
              <option value="user">User</option>
              <option value="session">Session</option>
            </select>
          </div>
          
          <div className="profile-editor-field">
            <label>Tags</label>
            <div className="profile-editor-array-field">
              <div className="profile-editor-array-items">
                {formData.tags.map((tag: string, index: number) => (
                  <span key={index} className="profile-editor-tag">
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeArrayItem('tags', index)}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                placeholder="Add tag..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addArrayItem('tags', e.currentTarget.value);
                    e.currentTarget.value = '';
                  }
                }}
              />
            </div>
          </div>
        </section>
        
        {/* Data Boundaries */}
        <section className="profile-editor-section">
          <h3>Data Boundaries</h3>
          
          <div className="profile-editor-field">
            <label>Allowed Datasets</label>
            <div className="profile-editor-array-field">
              <div className="profile-editor-array-items">
                {(formData.dataBoundaries.datasets || []).map((dataset: string, index: number) => (
                  <span key={index} className="profile-editor-tag">
                    {dataset}
                    <button
                      type="button"
                      onClick={() => removeArrayItem('dataBoundaries.datasets', index)}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                placeholder="Add dataset..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addArrayItem('dataBoundaries.datasets', e.currentTarget.value);
                    e.currentTarget.value = '';
                  }
                }}
              />
            </div>
          </div>
          
          <div className="profile-editor-field">
            <label>Required Tags</label>
            <div className="profile-editor-array-field">
              <div className="profile-editor-array-items">
                {(formData.dataBoundaries.tags || []).map((tag: string, index: number) => (
                  <span key={index} className="profile-editor-tag">
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeArrayItem('dataBoundaries.tags', index)}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                placeholder="Add required tag..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addArrayItem('dataBoundaries.tags', e.currentTarget.value);
                    e.currentTarget.value = '';
                  }
                }}
              />
            </div>
          </div>
          
          <div className="profile-editor-field-group">
            <div className="profile-editor-field">
              <label htmlFor="dateStart">Date Range Start</label>
              <input
                id="dateStart"
                type="date"
                value={formData.dataBoundaries.dateRange?.start || ''}
                onChange={(e) => updateField('dataBoundaries.dateRange.start', e.target.value)}
              />
            </div>
            
            <div className="profile-editor-field">
              <label htmlFor="dateEnd">Date Range End</label>
              <input
                id="dateEnd"
                type="date"
                value={formData.dataBoundaries.dateRange?.end || ''}
                onChange={(e) => updateField('dataBoundaries.dateRange.end', e.target.value)}
              />
            </div>
          </div>
        </section>
        
        {/* Perspective */}
        <section className="profile-editor-section">
          <h3>Perspective</h3>
          
          <div className="profile-editor-field-group">
            <div className="profile-editor-field">
              <label htmlFor="tone">Tone</label>
              <input
                id="tone"
                type="text"
                value={formData.perspective.tone || ''}
                onChange={(e) => updateField('perspective.tone', e.target.value)}
                placeholder="e.g., professional-friendly"
              />
            </div>
            
            <div className="profile-editor-field">
              <label htmlFor="audience">Audience</label>
              <input
                id="audience"
                type="text"
                value={formData.perspective.audience || ''}
                onChange={(e) => updateField('perspective.audience', e.target.value)}
                placeholder="e.g., B2B decision makers"
              />
            </div>
          </div>
          
          <div className="profile-editor-field-group">
            <div className="profile-editor-field">
              <label htmlFor="style">Style</label>
              <input
                id="style"
                type="text"
                value={formData.perspective.style || ''}
                onChange={(e) => updateField('perspective.style', e.target.value)}
                placeholder="e.g., conversational"
              />
            </div>
            
            <div className="profile-editor-field">
              <label htmlFor="language">Language</label>
              <input
                id="language"
                type="text"
                value={formData.perspective.language || ''}
                onChange={(e) => updateField('perspective.language', e.target.value)}
                placeholder="e.g., en-US"
              />
            </div>
          </div>
        </section>
        
        {/* Actions */}
        <div className="profile-editor-actions">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="profile-editor-button-secondary"
              disabled={saving}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="profile-editor-button-primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileEditor;
